// Profile is the "my page" — masthead + at-a-glance stats + collapsible
// About / Posts / Groups / Activity sections. Uses native <details> so
// we don't pull in an accordion library — CSS styles the summary marker.
//
// Posts are individually expandable (per-row <details>) with a Manage
// affordance: currently just Delete (POST edit needs a PATCH endpoint;
// tracked as a follow-up).

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useApi } from '../lib/api';
import { formatMastheadDate, timeAgo } from '../lib/format';
import { ReviewNotes } from '../components/ReviewNotes';
import { Avatar } from '../components/Avatar';
import { renderMarkdown } from '../lib/markdown';
import type { ActivityEntry, Community, Group, Me, Post } from '../types/api';

export function Profile() {
    const api = useApi();
    const { authed, login } = useAuth();
    const [me,          setMe]          = useState<Me | null>(null);
    const [communities, setCommunities] = useState<Community[]>([]);
    const [posts,       setPosts]       = useState<Post[]>([]);
    const [groups,      setGroups]      = useState<Group[]>([]);

    const load = useCallback(async () => {
        if (!authed) return;
        try {
            const [m, c, p, g] = await Promise.all([
                api.get<Me>('/api/me'),
                api.get<{ communities: Community[] }>('/api/communities'),
                api.get<{ posts: Post[]              }>('/api/posts?author=me&limit=50'),
                api.get<{ groups: Group[]            }>('/api/groups?mine=1'),
            ]);
            setMe(m);
            setCommunities(c.communities ?? []);
            setPosts(p.posts ?? []);
            setGroups(g.groups ?? []);
        } catch (e) { console.error('profile load failed', e); }
    }, [api, authed]);

    useEffect(() => { load(); }, [load]);

    async function deletePost(postID: string) {
        if (!window.confirm('Delete this post? This can\'t be undone.')) return;
        try {
            await api.del('/api/posts/' + postID);
            setPosts(posts.filter(p => p.id !== postID));
        } catch (e) { alert('Delete failed: ' + (e as Error).message); }
    }

    if (!authed) return (
        <div className="prelogin">
            <h1>Profile</h1>
            <p>Log in to see your profile.</p>
            <button type="button" className="plain" onClick={login}>Log in</button>
        </div>
    );
    if (!me) return <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Loading…</div>;

    const homeCommunity = communities.find(c => c.id === me.home_community_id);
    const recommendsReceived = posts.reduce((acc, p) => acc + (p.like_count ?? 0), 0);

    return (
        <main>
            <div className="profile-wrap">
                <div className="masthead">
                    <div className="rule-top" />
                    <div style={{ display: 'flex', gap: 20, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Avatar src={me.avatar_url} name={me.display_name} seed={me.id} size={80} />
                        <div>
                            <h1 style={{ margin: 0 }}>{me.display_name}</h1>
                            <div className="issue">
                                Member since {new Date(me.created_at).toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' })}
                            </div>
                        </div>
                    </div>
                    <div className="rule-bottom" />
                </div>

                <ReviewNotes />

                <ProfileStats
                    posts={posts.length}
                    groups={groups.length}
                    recommendsReceived={recommendsReceived}
                />

                <details className="profile-fold" open={false}>
                    <summary><h2>About you</h2><span className="fold-hint">tap to open</span></summary>
                    <dl className="kv">
                        <dt>Email</dt>              <dd>{me.email}</dd>
                        <dt>Display name</dt>       <dd>{me.display_name}</dd>
                        <dt>When you post anonymously, you appear as</dt>
                        <dd style={{ fontFamily: "'Newsreader',serif", fontStyle: 'italic' }}>
                            {me.anon_display_identity || '—'}
                        </dd>
                        <dt>Home community</dt>     <dd>{homeCommunity?.name || '— not set —'}</dd>
                        <dt>Member since</dt>       <dd>{formatMastheadDate(new Date(me.created_at))}</dd>
                    </dl>
                    <div style={{ marginTop: 12 }}>
                        <Link to="/settings" className="section-link">Edit in Settings →</Link>
                    </div>
                </details>

                <details className="profile-fold" open>
                    <summary><h2>Your posts ({posts.length})</h2><span className="fold-hint">tap a post to manage</span></summary>
                    {posts.length === 0 ? (
                        <p className="empty">You haven't posted yet. Hit the "+" up top to publish one.</p>
                    ) : posts.map(p => <MyPostRow key={p.id} post={p} onDelete={() => deletePost(p.id)} />)}
                </details>

                <details className="profile-fold" open={false}>
                    <summary>
                        <h2>Your groups &amp; communities ({groups.length})</h2>
                        <span className="fold-hint">tap to open</span>
                    </summary>
                    {groups.length === 0 ? (
                        <p className="empty">You're not a member of any groups yet. <Link to="/manage">Create or join one</Link>.</p>
                    ) : (
                        <>
                            {groups.map(g => (
                                <div className="group-row" key={g.id}>
                                    <span className="group-name">{g.name}</span>
                                    <span className="group-kind">{g.type}</span>
                                    {g.my_role && <span className={'group-role role-' + g.my_role}>{g.my_role}</span>}
                                </div>
                            ))}
                            <div style={{ marginTop: 12 }}>
                                <Link to="/manage" className="section-link">Manage groups & communities →</Link>
                            </div>
                        </>
                    )}
                </details>

                <details className="profile-fold" open={false}>
                    <summary><h2>Your activity</h2><span className="fold-hint">tap to open</span></summary>
                    <ActivityBody />
                </details>

                <div style={{ marginTop: 24, textAlign: 'center', fontSize: 12, color: '#666' }}>
                    <Link to="/settings" className="section-link">Open Settings →</Link>
                </div>
            </div>
        </main>
    );
}

// -----------------------------------------------------------------

function MyPostRow({ post, onDelete }: { post: Post; onDelete: () => void }) {
    const kindBadge = post.post_type === 'notice' ? 'Notice' :
                      post.post_type === 'event'  ? 'Event'  :
                      post.post_type === 'offer'  ? 'Offer'  : post.post_type;
    const title = post.title || '(untitled)';
    return (
        <details className="my-post-row">
            <summary>
                <span className="my-post-kind">{kindBadge}</span>
                <span className="my-post-title">{title}</span>
                <span className="my-post-meta">
                    {timeAgo(post.created_at)}
                    {typeof post.like_count === 'number' && post.like_count > 0 && (
                        <> · 👍 {post.like_count}</>
                    )}
                </span>
            </summary>
            <div className="my-post-body">
                {post.subhead && <p style={{ fontStyle: 'italic', color: '#555' }}>{post.subhead}</p>}
                <div dangerouslySetInnerHTML={{ __html: renderMarkdown(post.body || '') }} />
                <div className="my-post-actions">
                    <Link to={'/posts/' + post.id} className="section-link">View on paper →</Link>
                    <button type="button" onClick={onDelete} className="danger-btn">Delete post</button>
                </div>
            </div>
        </details>
    );
}

function ProfileStats(props: { posts: number; groups: number; recommendsReceived: number }) {
    const items: Array<[string, number | '—']> = [
        ['Posts',               props.posts],
        ['Groups',              props.groups],
        ['Likes received', props.recommendsReceived],
        ['Comments',            '—'],
    ];
    return (
        <section className="profile-stats">
            {items.map(([lbl, v]) => (
                <div className="stat" key={lbl}>
                    <strong>{v}</strong>
                    <span className="lbl">{lbl}</span>
                </div>
            ))}
        </section>
    );
}

function ActivityBody() {
    const api = useApi();
    const [entries, setEntries] = useState<ActivityEntry[]>([]);
    const [err,     setErr]     = useState('');

    useEffect(() => {
        api.get<{ entries: ActivityEntry[] }>('/api/me/activity?limit=20')
            .then(r => setEntries(r.entries ?? []))
            .catch(e => setErr((e as Error).message));
    }, [api]);

    if (err) return <p style={{ color: '#8b1919' }}>{err}</p>;
    if (entries.length === 0) return (
        <p style={{ color: '#999', fontStyle: 'italic', fontFamily: "'Newsreader',serif" }}>
            Your posts, comments, recommends, and group joins will appear here as you use OnHere.
        </p>
    );
    return <>
        {entries.map(e => (
            <div className="activity-row" key={e.id}>
                <span className="activity-kind">{humaniseKind(e.kind)}</span>
                <span className="activity-when">{timeAgo(e.created_at)}</span>
                {typeof e.body?.title === 'string' && <div className="activity-body">"{e.body.title as string}"</div>}
            </div>
        ))}
    </>;
}

function humaniseKind(k: string): string {
    return k.replace(/_/g, ' ').replace(/\b\w/g, m => m.toUpperCase());
}
